import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, FileText } from 'lucide-react';

const backendUrl =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8001'
    : '');

function formatCurrencyBR(value) {
  if (value == null || value === '') return '—';
  return Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

export default function PublicTreatmentAcceptancePage() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [data, setData] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError('');

        const res = await fetch(`${backendUrl}/api/treatments/public/acceptance/${token}`);

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Link inválido ou indisponível.');
        }

        const json = await res.json();
        setData(json);
        setAccepted(json.patient_acceptance_status === 'aceito');
      } catch (e) {
        setError(e.message || 'Erro ao carregar plano de tratamento.');
      } finally {
        setLoading(false);
      }
    }

    if (token) load();
  }, [token]);

  const handleConfirm = async () => {
    try {
      setConfirming(true);
      setError('');

      const res = await fetch(`${backendUrl}/api/treatments/public/acceptance/${token}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erro ao registrar aceite.');
      }

      setAccepted(true);
    } catch (e) {
      setError(e.message || 'Erro ao registrar aceite.');
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow p-6 text-gray-700">
          Carregando plano de tratamento...
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow p-6 max-w-lg w-full">
          <div className="flex items-center gap-2 text-red-600 mb-3">
            <AlertCircle className="w-5 h-5" />
            <h1 className="font-semibold">Não foi possível abrir este link</h1>
          </div>
          <p className="text-sm text-gray-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow border overflow-hidden">
        <div className="bg-green-700 text-white p-6">
          <div className="flex items-center gap-3">
            <FileText className="w-7 h-7" />
            <div>
              <h1 className="text-xl font-bold">Aceite do Plano de Tratamento</h1>
              <p className="text-sm text-green-100">
                Leia as informações antes de confirmar o aceite.
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {accepted && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3 text-green-800">
              <CheckCircle className="w-5 h-5 mt-0.5" />
              <div>
                <p className="font-semibold">Aceite já registrado.</p>
                <p className="text-sm">Esta confirmação ficou registrada no prontuário.</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="border rounded-xl p-3">
              <p className="text-gray-500 text-xs">Paciente</p>
              <p className="font-medium text-gray-900">{data?.patient_name || '—'}</p>
            </div>

            <div className="border rounded-xl p-3">
              <p className="text-gray-500 text-xs">Procedimento</p>
              <p className="font-medium text-gray-900">{data?.procedure || '—'}</p>
            </div>

            <div className="border rounded-xl p-3">
              <p className="text-gray-500 text-xs">Valor estimado</p>
              <p className="font-medium text-gray-900">{formatCurrencyBR(data?.cost)}</p>
            </div>

            <div className="border rounded-xl p-3">
              <p className="text-gray-500 text-xs">Status do plano</p>
              <p className="font-medium text-gray-900">{data?.status || '—'}</p>
            </div>
          </div>

          {data?.description && (
            <Section title="Descrição" text={data.description} />
          )}

          {data?.diagnosis && (
            <Section title="Diagnóstico / achado clínico" text={data.diagnosis} />
          )}

          {data?.treatment_plan && (
            <Section title="Plano de tratamento proposto" text={data.treatment_plan} />
          )}

          {data?.alternatives && (
            <Section title="Alternativas terapêuticas" text={data.alternatives} />
          )}

          {data?.risks && (
            <Section title="Riscos e limitações" text={data.risks} />
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-900">
            Ao confirmar, você declara que teve acesso ao plano de tratamento apresentado.
            Esta confirmação registra aceite eletrônico simples, com data, horário e dados técnicos do acesso.
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={handleConfirm}
            disabled={confirming || accepted}
            className="w-full bg-green-700 text-white rounded-xl py-3 font-semibold hover:bg-green-800 disabled:opacity-60"
          >
            {accepted ? 'Aceite registrado' : confirming ? 'Registrando...' : 'Li e aceito o plano de tratamento'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, text }) {
  return (
    <div>
      <p className="font-semibold text-gray-900 mb-2">{title}</p>
      <div className="border rounded-xl p-4 bg-gray-50 text-sm text-gray-800 whitespace-pre-wrap">
        {text}
      </div>
    </div>
  );
}